import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUID } from 'react-uid';
import { compose, layout, position, space } from 'styled-system';

import { isLastItem } from 'lib/array';
import { isObj } from 'lib/object';

import useClickOutside from '../../hooks/useClickOutside';
import useKeyDown from '../../hooks/useKeyDown';
import Button from '../Button';
import Icon from '../Icon';
import Input from '../Input';
import Overlay from '../Overlay';
import { useAutoComplete } from './Context/AutoCompleteProvider';

const FIRST_OPTIONS_INDEX = 0;
const UNDEFINED_OPTION_INDEX = -1;
const OPTION_HEIGHT_IN_PX = 48;
const OPTIONS_WRAPPER_MAX_SIZE_IN_PX = 272;

const StyledWrapper = styled('div')`
  ${({ full }) => css`
    width: ${full && '100%'};
    position: relative;
  `}

  ${compose(space, layout, position)}
`;

const StyledButton = styled(Button)`
  position: absolute;
  top: 8%;
  right: 0;
  border: none;

  &:hover {
    background: none;
    border: none;
  }

  ${({ hasLabel }) =>
    hasLabel &&
    css`
      top: 45%;
    `}
`;

const StyledOptionsWrapper = styled('div')`
  ${({ theme, widthOffset }) => css`
    background: ${theme.colors.autoComplete.background};
    border: 1px solid ${theme.colors.autoComplete.border};
    border-radius: ${theme.border.radius.size2};
    box-shadow: ${theme.shadows.size3};
    margin-top: ${theme.space[2]};
    position: absolute;
    width: ${widthOffset ? `calc(100% + ${widthOffset}px)` : '100%'};
    z-index: 60;
  `}
  ${layout}
`;

export const StyledList = styled('ul')`
  ${({ theme }) => css`
    list-style: none;
    padding: ${theme.space[2]} 0;
  `}
`;

const getInitialActiveOptionIndex = (sendOnlyOptions) =>
  sendOnlyOptions ? FIRST_OPTIONS_INDEX : UNDEFINED_OPTION_INDEX;

const getSendButtonMarginLeftValue = (element) => {
  if (!element) {
    return 0;
  }

  const marginLeft = getComputedStyle(element).getPropertyValue('margin-left');
  const result = marginLeft.replace(/\D/g, '');

  return Number(result);
};

const isRenderable = (children) => {
  if (Array.isArray(children)) {
    return children.some(isRenderable);
  }

  if (typeof children === 'boolean') {
    return false;
  }

  if (
    (typeof children === 'string' && !!children) ||
    typeof children === 'number'
  ) {
    return true;
  }

  return isObj(children);
};

const AutoCompleteContainer = forwardRef(
  (
    {
      children,
      cleanAfterSend,
      disabled,
      error,
      full,
      iconLeft,
      iconRight,
      id: customId,
      initialValue,
      label,
      maxOptions,
      message,
      options,
      overlay,
      placeholder,
      render,
      searchStartsAt,
      sendButtonRef,
      sendOnlyOptions,
      onBlur,
      onChange,
      onFocus,
      onResetClick,
      onSend,
      ...rest
    },
    ref
  ) => {
    const uid = useUID();
    const inputRef = useRef(null);
    const optionsWrapperRef = useRef(null);
    const wrapperRef = useRef(null);
    const [isFocused, setFocused] = useState(false);
    const { isOpen, setOpen } = useClickOutside(wrapperRef, () =>
      activeOptionIndex.setter(getInitialActiveOptionIndex(sendOnlyOptions))
    );
    const {
      activeOptionIndex,
      currInputValue,
      maxOptions: maxOptionsState,
      optionsValuesStore,
      utils,
      getInlineValues,
      optionsMapper,
    } = useAutoComplete();

    const internalOptions = useMemo(() => {
      if (disabled || children) {
        return [];
      }

      setOpen(true);

      return optionsMapper(options, maxOptions);
    }, [options, maxOptions]);

    const onSendHandler = (value) => {
      inputRef.current.blur();
      onSend(value);
    };

    useKeyDown(
      ['Enter', 'NumpadEnter'],
      () => {
        if (!isOpen && sendOnlyOptions) {
          return;
        }

        const isUndefinedActiveOptionIndex =
          activeOptionIndex.getter === UNDEFINED_OPTION_INDEX;

        if (
          currInputValue.getter.length < searchStartsAt &&
          isUndefinedActiveOptionIndex
        ) {
          return;
        }

        if (sendOnlyOptions && isUndefinedActiveOptionIndex) {
          return;
        }

        let value = null;

        if (sendOnlyOptions || !isUndefinedActiveOptionIndex) {
          if (internalOptions.length !== 0) {
            value = internalOptions[activeOptionIndex.getter].value;
          }

          const optionValuesFromListChildren = getInlineValues();

          if (optionValuesFromListChildren.length !== 0) {
            value = optionValuesFromListChildren[activeOptionIndex.getter];
          }
        } else {
          value = currInputValue.getter.trim();
        }

        if (!value) {
          return;
        }

        currInputValue.setter(cleanAfterSend ? '' : value);
        onSendHandler(value);
        onResetStates();
      },
      { targetRef: wrapperRef, enabled: isFocused }
    );

    useKeyDown(
      'ArrowUp',
      () => {
        if (!isOpen) {
          return;
        }

        if (
          activeOptionIndex.getter === FIRST_OPTIONS_INDEX ||
          activeOptionIndex.getter === UNDEFINED_OPTION_INDEX
        ) {
          const values = getInlineValues();

          activeOptionIndex.setter(values.length - 1);
          return;
        }

        activeOptionIndex.setter((prev) => prev - 1);
      },
      { enabled: isFocused }
    );

    useKeyDown(
      'ArrowDown',
      () => {
        if (isOpen) {
          const values = getInlineValues();

          if (
            isLastItem(values, activeOptionIndex.getter) ||
            activeOptionIndex.getter === UNDEFINED_OPTION_INDEX
          ) {
            if (optionsWrapperRef.current?.scrollTop !== undefined) {
              optionsWrapperRef.current.scrollTop = 0;
            }

            activeOptionIndex.setter(FIRST_OPTIONS_INDEX);
            return;
          }

          activeOptionIndex.setter((prevState) => {
            const nextState = prevState + 1;

            if (optionsWrapperRef.current?.scrollTop !== undefined) {
              const nextActiveOptionsHeight = nextState * OPTION_HEIGHT_IN_PX;

              /**
               * is subtracted the height of a option from the options wrapper
               * height to force a scroll when the selected option is showed but
               * not completely
               */
              if (
                nextActiveOptionsHeight >
                OPTIONS_WRAPPER_MAX_SIZE_IN_PX - OPTION_HEIGHT_IN_PX
              ) {
                optionsWrapperRef.current.scrollTop = nextActiveOptionsHeight;
              }
            }

            return nextState;
          });
        }
      },
      { enabled: isFocused }
    );

    const onResetStates = useCallback(() => {
      setOpen(false);
      activeOptionIndex.setter(getInitialActiveOptionIndex(sendOnlyOptions));
    }, []);

    useKeyDown('Escape', onResetStates);

    useEffect(() => {
      if (!isFocused) {
        currInputValue.setter(initialValue);
      }
    }, [initialValue]);

    useEffect(() => {
      if (options.length === 0 && !children) {
        onResetStates();
      }
    }, [options, maxOptions]);

    useEffect(() => {
      activeOptionIndex.setter(getInitialActiveOptionIndex(sendOnlyOptions));
    }, [sendOnlyOptions]);

    useEffect(() => {
      if (!children || !maxOptions) {
        return maxOptionsState.setter(maxOptions);
      }

      const validChildrenLength =
        Children.toArray(children).filter(isValidElement).length;

      const maxOptionsPerListChild =
        validChildrenLength > 0
          ? Math.floor(maxOptions / validChildrenLength)
          : maxOptions;

      maxOptionsState.setter(maxOptionsPerListChild);
    }, [maxOptions, children]);

    useEffect(() => {
      const optionsValues = internalOptions.map(({ value }) => value);

      optionsValuesStore.setter((prevState) => ({
        ...prevState,
        global: optionsValues,
      }));
    }, [internalOptions]);

    useEffect(() => {
      const onOptionClick = (value) => {
        currInputValue.setter(cleanAfterSend ? '' : value);
        onSendHandler(value);
        onResetStates();
      };

      utils.setter({
        onOptionClick,
        onResetStates: () => {},
        onSend,
      });
    }, [cleanAfterSend, onSend, onResetStates]);

    useImperativeHandle(
      ref,
      () => ({
        ...inputRef.current,
        /**
         * cleanup the input and other states when component is used on side a
         * send button
         */
        cleanup: () => {
          onResetStates();
          currInputValue.setter('');
        },
      }),
      [inputRef, currInputValue.getter, onResetStates]
    );

    const onBlurHandler = (event) => {
      setFocused(false);
      onBlur(event);
    };

    const onChangeHandler = (event) => {
      const { value } = event.target;

      if (sendOnlyOptions && internalOptions.length > 0) {
        activeOptionIndex.setter(FIRST_OPTIONS_INDEX);
      }

      currInputValue.setter(value);
      onChange(event);
    };

    const onFocusHandler = (event) => {
      if (!isOpen) {
        setOpen(true);
      }

      setFocused(true);
      onFocus(event);
    };

    const onResetClickHandler = () => {
      currInputValue.setter('');
      onResetStates();
      onResetClick();
    };

    const id = customId || uid;
    const isShowingOverlay = overlay && isOpen;
    const sendButtonWidthOffset =
      sendButtonRef.current?.offsetWidth +
      getSendButtonMarginLeftValue(sendButtonRef.current);

    return (
      <>
        {isShowingOverlay && (
          <Overlay display={['none', null, null, 'block']} full />
        )}
        <StyledWrapper
          ref={wrapperRef}
          zIndex={isShowingOverlay ? 60 : null}
          full={full}
          {...rest}
        >
          <Input
            ref={inputRef}
            id={id}
            disabled={disabled}
            error={error}
            iconLeft={!currInputValue.getter ? iconLeft : null}
            iconRight={!currInputValue.getter ? iconRight : null}
            label={label}
            message={message}
            placeholder={placeholder}
            value={currInputValue.getter}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            full
          />
          {currInputValue.getter && (
            <StyledButton
              type="ghost"
              size="size3"
              hasLabel={!!label}
              aria-label="limpar campo de busca"
              data-test-selector="cleanup-input-btn"
              icon={<Icon name="close" />}
              onClick={onResetClickHandler}
            />
          )}
          {!children && isOpen && internalOptions.length > 0 && (
            <StyledOptionsWrapper
              ref={optionsWrapperRef}
              maxHeight={272}
              widthOffset={sendButtonWidthOffset}
              overflowY="auto"
            >
              <StyledList
                aria-describedby="listagem de buscas encontradas ao seu termo pesquisado"
                data-test-selector="autocomplete-options-list"
              >
                {/* TODO this can be improved */}
                {internalOptions.map(({ text, value, ...props }) =>
                  cloneElement(render({ text, ...props }), {
                    key: value,
                    value,
                  })
                )}
              </StyledList>
            </StyledOptionsWrapper>
          )}
          {isOpen && isRenderable(children) && (
            <StyledOptionsWrapper widthOffset={sendButtonWidthOffset}>
              {children}
            </StyledOptionsWrapper>
          )}
        </StyledWrapper>
      </>
    );
  }
);

AutoCompleteContainer.defaultProps = {
  children: null,
  cleanAfterSend: false,
  disabled: false,
  error: false,
  full: false,
  initialValue: '',
  maxOptions: null,
  options: [],
  overlay: false,
  placeholder: null,
  searchStartsAt: 2,
  sendButtonRef: { current: null },
  sendOnlyOptions: false,
  render: () => {},
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  onResetClick: () => {},
  onSend: () => {},
};

AutoCompleteContainer.propTypes = {
  /**
   * Components to be rendered inside component
   */
  children: PropTypes.node,
  /**
   * Decides if the input are cleaned after the `
   * send` action occur
   */
  cleanAfterSend: PropTypes.bool,
  /**
   * Disable the autocomplete
   */
  disabled: PropTypes.bool,
  /**
   * Changes the autocomplete state to the error
   */
  error: PropTypes.bool,
  /**
   * Flex fills the parent component's width
   */
  full: PropTypes.bool,
  /**
   * Renders an icon on the left side of input
   */
  iconLeft: PropTypes.shape(),
  /**
   * Renders an icon on the right side of input
   */
  iconRight: PropTypes.shape(),
  /**
   * Set a initial value for the input
   */
  initialValue: PropTypes.string,
  /**
   * Set custom id in the input
   */
  id: PropTypes.string,
  /**
   * Text to be rendered as a label
   */
  label: PropTypes.string,
  /**
   * Set the max quantity of options that will be render
   */
  maxOptions: PropTypes.number,
  /**
   * Support message to be rendered below the input field
   */
  message: PropTypes.string,
  /**
   * Set the options that will be render
   */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ])
  ),
  /**
   * Decides if render component with a overlay
   */
  overlay: PropTypes.bool,
  /**
   * Text that appears in the form control when the it is empty
   */
  placeholder: PropTypes.string,
  /**
   * Callback fn used to render each autocomplete option
   */
  render: PropTypes.func,
  /**
   * Set the min characters quantity that enabled `send` event
   */
  searchStartsAt: PropTypes.number,
  /**
   * Decides if the user can choose only the options or not
   */
  sendOnlyOptions: PropTypes.bool,
  /**
   * The `ref` of send button to options list occupy full width
   * - obs: send button width included
   */
  sendButtonRef: PropTypes.shape(),
  /**
   * Callback fn to be executed on `onBlur` event
   */
  onBlur: PropTypes.func,
  /**
   * Callback fn to be executed on `onChange` event
   */
  onChange: PropTypes.func,
  /**
   * Callback fn to be executed on `onFocus` event
   */
  onFocus: PropTypes.func,
  /**
   * Callback fn to be executed on `onResetClick` event
   */
  onResetClick: PropTypes.func,
  /**
   * Callback fn to be executed on `onSend` event
   */
  onSend: PropTypes.func,
};

AutoCompleteContainer.displayName = 'AutoCompleteContainer';

export default AutoCompleteContainer;
